import Decimal from "decimal.js";

export function indexValueCalculator(principalAmount, annualRate, yearsInvested) {
    
    const years = parseInt(yearsInvested);
    const monthlyRate = annualRate.div(100).add(1).pow(Decimal.div(1, 12));

    let amount = principalAmount;
    let values = [];

    for (let month = 0; month <= years*12; month++) {
        if (month > 0) {
            amount = amount.mul(monthlyRate);
        }
        let rate = amount.div(principalAmount).sub(1).mul(100);
        values.push({ amount: amount.toDecimalPlaces(2), rate: rate.toDecimalPlaces(2) });
    }

    return values;
}

export function segmentValueCalculator(principalAmount, annualRate, fund) {

    const segmentType = fund.SegmentMapping;
    const yearsInvested = parseInt(fund.Years);
    const annualFeeRate = fund.AnnualFeeRate ? new Decimal(fund.AnnualFeeRate) : null;
    const yieldRate = fund.Yield ? new Decimal(fund.Yield) : null;
    const yieldInterestRate = fund.YieldInterest ? new Decimal(fund.YieldInterest) : null;
   
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]; 
    const curDate = new Date();
    
    let curYear = curDate.getFullYear();
    let curMonth = curDate.getMonth();
    let startingMonth = curMonth === 11 ? 0 : curMonth + 1;
    let yearNum = 0;

    let amount = principalAmount;
    let creditAmount = new Decimal('0');
    let values = [];

    let monthlyRates = [];
    let applyCredits = 0;

    if (segmentType.includes('Milestone Lock')) {
        monthlyRates = milestoneLockRateCalculator(annualRate, fund);
    } else if (segmentType === 'Dual Directional Yield') {
        const yieldArray = dualDirectionalYieldCalculator(annualRate, fund);
        monthlyRates = yieldArray[0];
        applyCredits = yieldArray[1];
    } else {
        monthlyRates = rateCalculator(annualRate, fund);
    }

    let monthlyRate = monthlyRates[0];

    for (let month = 0; month <= yearsInvested*12; month++) {
        if (curMonth === startingMonth) {
            if (yearNum > 0 && yearNum in monthlyRates) {
                monthlyRate = monthlyRates[yearNum];
            }
            if (annualFeeRate) {
                amount = amount.sub(annualFeeRate.div(100).mul(principalAmount));
            } 
            yearNum++;
        }
        if (month > 0) {
            if (yieldInterestRate) {
                creditAmount = creditAmount.mul(yieldInterestRate.div(100).add(1).pow(Decimal.div(1,12)));
            }
            if (month % 3 === 0 && applyCredits >= month / 3) {
                creditAmount = creditAmount.add(principalAmount.div(4).mul(yieldRate.div(100)))
            }
            amount = amount.mul(monthlyRate);
        }
        let rate = amount.add(creditAmount).div(principalAmount).sub(1).mul(100);
        values.push({ year: curYear, month: months[curMonth], amount: amount.add(creditAmount).toDecimalPlaces(2), rate: rate.toDecimalPlaces(2) });
        if (curMonth === 11) {
            curYear++;
            curMonth = 0;
        } else {
            curMonth++;
        }
    }

    return values;
};

const dualDirectionalYieldCalculator = (annualRate, fund) => {

    const years = parseInt(fund.Years);
    const buffer = fund.Buffer ? new Decimal(fund.Buffer) : null;

    let growthRate = annualRate.div(100).add(1).pow(years).sub(1).mul(100);
    let quarterRate = annualRate.div(100).add(1).pow(Decimal.div(1,4));
    let monthlyRates = [];
    let applyCredits = 0;

    if (growthRate.lessThan(0)) {
        for (let quarter = 1; quarter <= years*4; quarter++) {
            let curQuarterRate = quarterRate.mul(quarterRate.pow(quarter - 1));
            if (curQuarterRate.sub(1).mul(100).neg().lessThanOrEqualTo(buffer)) {
                applyCredits++;
            } else {
                break;
            }
        }
        
        growthRate = calcNegativeStandardSegment(growthRate, buffer)
        monthlyRates[0] = growthRate.div(100).add(1).pow(Decimal.div(1,years * 12));
    } else {
        monthlyRates[0] = 1;
        applyCredits = 4 * years;
    }

    return [monthlyRates, applyCredits];
}

const milestoneLockRateCalculator = (annualRate, fund) => {

    const years = parseInt(fund.Years);
    const buffer = fund.Buffer ? new Decimal(fund.Buffer) : null;
    const floor = fund.Floor ? new Decimal(fund.Floor) : null;
    const downsideParticipation = fund.DownsideParticipation ? new Decimal(fund.DownsideParticipation) : null;
    const cap = fund.Cap && fund.Cap !== 'Uncapped' ? new Decimal(fund.Cap) : null;
    const participation = fund.Participation ? new Decimal(fund.Participation) : null;
    const milestone = new Decimal(fund.Milestone);

    let growthRate = annualRate.div(100).add(1).pow(years).sub(1).mul(100);
    let monthlyRates = [];

    if (growthRate.lessThanOrEqualTo(0)) {
        growthRate = calcNegativeStandardSegment(growthRate, buffer, floor, downsideParticipation);
        let monthlyRate = growthRate.div(100).add(1).pow(Decimal.div(1,years * 12));
        monthlyRates[0] = monthlyRate;
    } else {
        let lockedYear = 0;
        for (let year = 1; year <= years; year++) {
            let growthRate = annualRate.div(100).add(1).pow(year - lockedYear).sub(1).mul(100);
            if (growthRate.greaterThanOrEqualTo(milestone) || year === years) {
                growthRate = calcPositiveStandardSegment(growthRate, cap, participation);
                let monthlyRate = growthRate.div(100).add(1).pow(Decimal.div(1,(year - lockedYear) * 12));
                monthlyRates[lockedYear] = monthlyRate;
                lockedYear = year;
            } 
        }
    }

    return monthlyRates;
};

const rateCalculator = (annualRate, fund) => {

    const segmentType = fund.SegmentMapping;
    const years = parseInt(fund.Years);
    const buffer = fund.Buffer ? new Decimal(fund.Buffer) : null;
    const floor = fund.Floor ? new Decimal(fund.Floor) : null;
    const downsideParticipation = fund.DownsideParticipation ? new Decimal(fund.DownsideParticipation) : null;
    const dual = fund.Dual ? new Decimal(fund.Dual) : null;
    const cap = fund.Cap && fund.Cap !== 'Uncapped' ? new Decimal(fund.Cap) : null;
    const step = fund.Step ? new Decimal(fund.Step) : null;
    const participation = fund.Participation ? new Decimal(fund.Participation) : null;
    const participation2 = fund.Participation2 ? new Decimal(fund.Participation2) : null;
    const boost = fund.Boost ? new Decimal(fund.Boost) : null;
    const spread = fund.Spread ? new Decimal(fund.Spread) : null;
    const tier = fund.Tier ? new Decimal(fund.Tier) : null;
    const protection = fund.Protection ? new Decimal(fund.Protection) : null;

    let monthlyRates = [];

    let growthRate = segmentType.includes('Annual Lock') ? annualRate : annualRate.div(100).add(1).pow(years).sub(1).mul(100);

    if (growthRate.lessThan(0)) {
        switch (segmentType) {
            case 'Dual Step Up Segment':
            case 'Dual Directional Trigger and Cap':
                growthRate = calcNegativeDualStepUpSegment(growthRate, buffer, step);
                break;
            case 'Dual Direction Segment':
                growthRate = calcNegativeDualDirectionSegment(growthRate, buffer);
                break;
            case 'Loss Limiter Segment':
                growthRate = calcNegativeLossLimiterSegment(growthRate, buffer, protection);
                break;
            case 'Performance Boost Cap Rate':
                growthRate = calcNegativePerformanceBoostCapRate(growthRate, buffer, boost);
                break;
            case 'Dual Performance Trigger':
                growthRate = calcNegativeDualPerformaceTrigger(growthRate, buffer, step);
                break;
            case 'Dual Plus':
                growthRate = calcNegativeDualPlus(growthRate, dual);
                break;
            case 'Peak Buffer Segment':
                growthRate = calcNegativePeakBufferSegment(growthRate, buffer);
                break;
            default:
                growthRate = calcNegativeStandardSegment(growthRate, buffer, floor, downsideParticipation);
        }
    } else {
        switch (segmentType) {
            case 'Step Up Segment':
            case 'Dual Step Up Segment':
            case 'Dual Performance Trigger':
                growthRate = calcPositiveStepUpSegment(growthRate, step);
                break;
            case 'Performance Boost Cap Rate':
                growthRate = calcPositivePerformanceBoostCapRate(growthRate, cap, boost);
                break;
            case 'Dual Plus':
                growthRate = calcPositiveDualPlus(growthRate, dual, cap);
                break;
            case 'Enhanced Cap Rate':
                growthRate = calcPositiveEnhancedCapRate(growthRate, cap, spread);
                break;
            case 'Step Rate Plus':
                growthRate = calcPositiveStepRatePlus(growthRate, step, participation);
                break;
            case 'Tiered Participation Rate':
                growthRate = calcPositiveTieredParticipationRate(growthRate, participation, participation2, tier);
                break;
            case "Dual Directional Trigger and Cap":
                growthRate = calcPositiveDualDirectionalTriggerAndCap(growthRate, step, cap);
                break;
            default:
                growthRate = calcPositiveStandardSegment(growthRate, cap, participation);
        }
    }

    let monthlyRate = segmentType.includes('Annual Lock') ? growthRate.div(100).add(1).pow(Decimal.div(1,12))
        : growthRate.div(100).add(1).pow(Decimal.div(1, years * 12));

    monthlyRates[0] = monthlyRate;

    return monthlyRates;

};

const calcNegativeStandardSegment = (growthRate, buffer, floor, downsideParticipation) => {
    if (buffer) {
        return Decimal.min(growthRate.add(buffer), 0);
    } else if (floor) {
        return Decimal.max(growthRate, floor.neg());
    } else {
        return growthRate.mul(downsideParticipation).div(100);
    }
}

const calcNegativePeakBufferSegment = (growthRate, buffer) => {
    if (growthRate.neg().lessThanOrEqualTo(buffer.div(2))) {
        return growthRate.neg();
    } else {
        return growthRate.add(buffer);
    }
}

const calcNegativeDualPerformaceTrigger = (growthRate, buffer, step) => {
    if (growthRate.neg().lessThanOrEqualTo(buffer)) {
        return step;
    } else {
        return growthRate.add(step);
    }
}

const calcNegativeDualStepUpSegment = (growthRate, buffer, step) => {
    if (growthRate.neg().lessThanOrEqualTo(buffer)) {
        return step;
    } else {
        return growthRate.add(buffer);
    }
}

const calcNegativeDualPlus = (growthRate, dual) => {
    return growthRate.add(dual);
}

const calcNegativeDualDirectionSegment = (growthRate, buffer) => {
    if (growthRate.neg().lessThanOrEqualTo(buffer)) {
        return growthRate.neg();
    } else {
        return growthRate.add(buffer);
    }
}

const calcNegativeLossLimiterSegment = (growthRate, buffer, protection) => {
    return Decimal.max(Decimal.min(growthRate.add(buffer), 0), protection.sub(100));
}

const calcNegativePerformanceBoostCapRate = (growthRate, buffer, boost) => {
    if (growthRate.neg().lessThanOrEqualTo(buffer)) {
        return Decimal.max(growthRate.add(boost), 0);
    } else {
        return growthRate.add(buffer);
    }
}

const calcPositiveStandardSegment = (growthRate, cap, participation) => {
    if (participation) {
        growthRate = growthRate.mul(participation).div(100);
    }

    if (!cap || growthRate.lessThan(cap)) {
        return growthRate;
    } else {
        return cap;
    }
}

const calcPositiveStepUpSegment = (growthRate, step) => {
    return step;
}

const calcPositiveDualPlus = (growthRate, dual, cap) => {
    if (!cap || growthRate.lessThan(cap)) {
        return Decimal.max(growthRate,dual);
    } else {
       return cap;
    }
}

const calcPositiveEnhancedCapRate = (growthRate, cap, spread) => {
    if (!cap || growthRate.sub(spread).lessThan(cap)) {
        return Decimal.max(growthRate.sub(spread), 0);
    } else {
        return cap;
    }
}

const calcPositiveStepRatePlus = (growthRate, step, participation) => {
    return Decimal.max(growthRate.mul(participation).div(100), step);
}

const calcPositivePerformanceBoostCapRate = (growthRate, cap, boost) => {
    if (!cap || growthRate.add(boost).lessThan(cap)) {
        return growthRate.add(boost);
    } else {
        return cap;
    }
}

const calcPositiveTieredParticipationRate = (growthRate, participation, participation2, tier) => {
    if (growthRate.lessThanOrEqualTo(tier)) {
        return growthRate.mul(participation).div(100);
    } else {
        const tierReturn = tier.mul(participation).div(100);
        const excessReturn = growthRate.sub(tier).mul(participation2).div(100);
        return tierReturn.add(excessReturn);
    }
}

const calcPositiveDualDirectionalTriggerAndCap = (growthRate, step, cap) => {
    return Decimal.max(Decimal.min(growthRate,cap), step);
}